// GitHub API service for fetching and uploading photos
// GitHub token should be stored in environment variable: REACT_APP_GITHUB_TOKEN
// GitHub repository: owner/repo format (e.g., "username/repo-name")

const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN || '';
const GITHUB_OWNER = process.env.REACT_APP_GITHUB_OWNER || '';
const GITHUB_REPO = process.env.REACT_APP_GITHUB_REPO || '';
const GITHUB_BRANCH = process.env.REACT_APP_GITHUB_BRANCH || 'main';
const BASE_URL = `https://api.github.com/repos/${GITHUB_OWNER}/${GITHUB_REPO}`;
const RAW_BASE_URL = `https://raw.githubusercontent.com/${GITHUB_OWNER}/${GITHUB_REPO}/${GITHUB_BRANCH}`;

// Convert GitHub API download_url to faster raw.githubusercontent.com URL
const getRawImageUrl = (path) => {
  return `${RAW_BASE_URL}/${path}`;
};

// Helper function to make authenticated requests
const makeRequest = async (url, options = {}) => {
  // Check if GitHub credentials are configured
  if (!GITHUB_TOKEN || !GITHUB_OWNER || !GITHUB_REPO) {
    throw new Error('GitHub credentials not configured. Please set REACT_APP_GITHUB_TOKEN, REACT_APP_GITHUB_OWNER, and REACT_APP_GITHUB_REPO in your .env file.');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `token ${GITHUB_TOKEN}`,
      'Accept': 'application/vnd.github.v3+json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `GitHub API error: ${response.status}`);
  }

  return response.json();
};

// Get all folders in a directory (clients)
export const getClientFolders = async (path = 'clients') => {
  try {
    const url = `${BASE_URL}/contents/${path}`;
    const contents = await makeRequest(url);
    return contents.filter(item => item.type === 'dir').map(item => ({
      name: item.name,
      path: item.path,
    }));
  } catch (error) {
    console.error('Error fetching client folders:', error);
    return [];
  }
};

// Get all images from a client folder (supports both direct images and event folders)
export const getClientImages = async (clientFolderName, eventName = null) => {
  try {
    const path = eventName 
      ? `clients/${clientFolderName}/${eventName}`
      : `clients/${clientFolderName}`;
    const url = `${BASE_URL}/contents/${path}`;
    const contents = await makeRequest(url);
    
    const imageFiles = contents.filter(item => 
      item.type === 'file' && 
      /\.(jpg|jpeg|png|gif|webp)$/i.test(item.name)
    );

    return imageFiles.map(item => ({
      name: item.name,
      url: getRawImageUrl(item.path), // Use raw.githubusercontent.com for faster loading
      downloadUrl: item.download_url, // Keep original for reference
      path: item.path,
      size: item.size,
      eventName: eventName || null,
    }));
  } catch (error) {
    console.error(`Error fetching images for ${clientFolderName}${eventName ? `/${eventName}` : ''}:`, error);
    return [];
  }
};

// Get all event folders for a client
export const getClientEvents = async (clientFolderName) => {
  try {
    const url = `${BASE_URL}/contents/clients/${clientFolderName}`;
    const contents = await makeRequest(url);
    return contents.filter(item => item.type === 'dir').map(item => ({
      name: item.name,
      path: item.path,
    }));
  } catch (error) {
    console.error(`Error fetching events for ${clientFolderName}:`, error);
    return [];
  }
};

// Get cover page images
export const getCoverImages = async () => {
  try {
    const url = `${BASE_URL}/contents/covers`;
    const contents = await makeRequest(url);
    
    const imageFiles = contents.filter(item => 
      item.type === 'file' && 
      /\.(jpg|jpeg|png|gif|webp)$/i.test(item.name)
    );

    return imageFiles.map(item => ({
      name: item.name,
      url: getRawImageUrl(item.path), // Use raw.githubusercontent.com for faster loading
      downloadUrl: item.download_url, // Keep original for reference
      path: item.path,
    }));
  } catch (error) {
    console.error('Error fetching cover images:', error);
    return [];
  }
};

// Upload image to GitHub
export const uploadImage = async (file, folderPath, fileName) => {
  try {
    // Convert file to base64
    const base64Content = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Check if file already exists
    let sha = null;
    try {
      const checkUrl = `${BASE_URL}/contents/${folderPath}/${fileName}`;
      const existingFile = await makeRequest(checkUrl);
      sha = existingFile.sha;
    } catch (e) {
      // File doesn't exist, that's okay
    }

    // Upload file
    const uploadUrl = `${BASE_URL}/contents/${folderPath}/${fileName}`;
    const result = await makeRequest(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: `Upload ${fileName}`,
        content: base64Content,
        ...(sha && { sha }), // Include sha if updating existing file
      }),
    });

    return {
      success: true,
      url: result.content.download_url,
      path: result.content.path,
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Upload client photo (supports events)
export const uploadClientPhoto = async (file, clientFolderName, eventName = null) => {
  const folderPath = eventName 
    ? `clients/${clientFolderName}/${eventName}`
    : `clients/${clientFolderName}`;
  return uploadImage(file, folderPath, file.name);
};

// Upload cover photo
export const uploadCoverPhoto = async (file) => {
  return uploadImage(file, 'covers', file.name);
};

// Get all clients with their images and events (optimized parallel fetching)
export const getAllClients = async () => {
  try {
    const folders = await getClientFolders('clients');
    
    // Fetch all client data in parallel for faster loading
    const clientPromises = folders.map(async (folder) => {
      try {
        // Fetch events and direct images in parallel
        const [events, directImages] = await Promise.all([
          getClientEvents(folder.name),
          getClientImages(folder.name)
        ]);
        
        // Fetch images from all events in parallel
        const eventImagesPromises = events.map(async (event) => {
          const images = await getClientImages(folder.name, event.name);
          return {
            eventName: event.name,
            displayEventName: event.name.split('-').map(name => 
              name.charAt(0).toUpperCase() + name.slice(1)
            ).join(' '),
            images,
          };
        });
        
        const eventImages = await Promise.all(eventImagesPromises);
        
        // Combine all images
        const allImages = [
          ...directImages.map(img => ({ ...img, eventName: null })),
          ...eventImages.flatMap(event => 
            event.images.map(img => ({ ...img, eventName: event.eventName }))
          )
        ];
        
        return {
          ...folder,
          images: allImages,
          events: eventImages.filter(e => e.images.length > 0),
          hasDirectImages: directImages.length > 0,
          displayName: folder.name.split('-').map(name => 
            name.charAt(0).toUpperCase() + name.slice(1)
          ).join(' & '),
        };
      } catch (error) {
        console.error(`Error fetching client ${folder.name}:`, error);
        return null;
      }
    });
    
    const clients = await Promise.all(clientPromises);
    return clients.filter(client => client !== null && client.images.length > 0);
  } catch (error) {
    console.error('Error fetching all clients:', error);
    return [];
  }
};
